import Phaser from "phaser";

/**
 * The defendable base at the path end. Visually a stone keep with a banner
 * and an HP-shaped glow that dims as lives drop. When a creep leaks, call
 * `onLeakHit(damage)` to play a hit reaction (shake + flash + crack).
 */
export class Throne {
  readonly container: Phaser.GameObjects.Container;
  private banner: Phaser.GameObjects.Triangle;
  private aura: Phaser.GameObjects.Arc;
  private hpRing: Phaser.GameObjects.Arc;
  private cracks: Phaser.GameObjects.Graphics;
  private maxLives: number;

  constructor(private scene: Phaser.Scene, x: number, y: number, maxLives: number) {
    this.maxLives = maxLives;
    this.container = scene.add.container(x, y);

    // Color palette — marble + gold + sapphire
    const STONE = 0x9a9aa2;
    const STONE_DK = 0x4a4a52;
    const STONE_HI = 0xc8c8d0;
    const MARBLE = 0xe8e8ee;
    const MARBLE_VEIN = 0x9a8a78;
    const GOLD = 0xffd84a;
    const GOLD_DK = 0xb8862a;
    const GOLD_HI = 0xfff080;
    const SAPPHIRE = 0x4cc9f0;
    const SAPPHIRE_HI = 0xa6e3ff;
    const RUBY = 0xff3050;

    // Aura kept for HP-color logic but hidden (no decorative ring).
    this.aura = scene.add.circle(0, 0, 28, SAPPHIRE, 0);
    this.aura.setVisible(false);

    // HP ring kept hidden at full HP; revealed when damaged.
    this.hpRing = scene.add.arc(0, 0, 24, 0, 360, false, 0x4ce053, 0);
    this.hpRing.setStrokeStyle(3, 0x4ce053, 0);
    this.hpRing.setVisible(false);

    // Shadow (wider, multi-layer for depth)
    const shadow = scene.add.ellipse(0, 18, 44, 9, 0x000000, 0.55);
    const shadowInner = scene.add.ellipse(0, 18, 36, 6, 0x000000, 0.6);

    // ---- 3-TIER MARBLE PEDESTAL ----
    // Bottom step (widest)
    const step1 = scene.add.rectangle(0, 16, 42, 5, MARBLE).setStrokeStyle(0.8, GOLD_DK, 1);
    const step1Hi = scene.add.rectangle(0, 14.5, 38, 0.8, GOLD_HI, 0.95);
    // Middle step
    const step2 = scene.add.rectangle(0, 12, 36, 4.5, MARBLE).setStrokeStyle(0.7, GOLD_DK, 1);
    const step2Hi = scene.add.rectangle(0, 10.5, 32, 0.7, GOLD_HI, 0.9);
    // Top step (becomes the new base)
    const base = scene.add.rectangle(0, 7, 30, 7, MARBLE).setStrokeStyle(1, GOLD_DK, 1);
    const baseVein1 = scene.add.line(0, 0, -10, 6, -2, 9, MARBLE_VEIN, 0.55).setLineWidth(0.6).setOrigin(0, 0);
    const baseVein2 = scene.add.line(0, 0, 4, 5, 11, 9, MARBLE_VEIN, 0.5).setLineWidth(0.5).setOrigin(0, 0);
    const baseTrim = scene.add.rectangle(0, 4, 30, 1.4, GOLD).setStrokeStyle(0.4, GOLD_DK, 1);

    // ---- ORNATE TOWER BODY ----
    // Side marble pillars (premium look)
    const pillarL = scene.add.rectangle(-13, -2, 4, 16, MARBLE).setStrokeStyle(0.7, GOLD_DK, 1);
    const pillarR = scene.add.rectangle(13, -2, 4, 16, MARBLE).setStrokeStyle(0.7, GOLD_DK, 1);
    const pillarLcap = scene.add.rectangle(-13, -10, 6, 1.6, GOLD).setStrokeStyle(0.4, GOLD_DK, 1);
    const pillarRcap = scene.add.rectangle(13, -10, 6, 1.6, GOLD).setStrokeStyle(0.4, GOLD_DK, 1);
    const pillarLbase = scene.add.rectangle(-13, 5, 6, 1.6, GOLD).setStrokeStyle(0.4, GOLD_DK, 1);
    const pillarRbase = scene.add.rectangle(13, 5, 6, 1.6, GOLD).setStrokeStyle(0.4, GOLD_DK, 1);

    // Tower body — keep stone but add gold accents
    const body = scene.add.rectangle(0, -3, 22, 22, STONE).setStrokeStyle(1.2, STONE_DK, 1);
    const bodyHi = scene.add.rectangle(-7, -7, 4, 14, STONE_HI, 0.5);
    // Gold horizontal trim bands
    const trim1 = scene.add.rectangle(0, -10, 22, 1.4, GOLD).setStrokeStyle(0.4, GOLD_DK, 1);
    const trim2 = scene.add.rectangle(0, 4, 22, 1.4, GOLD).setStrokeStyle(0.4, GOLD_DK, 1);

    // Ornate diamond inset on body face (ruby in gold setting)
    const insetGold = scene.add.polygon(0, -3, [0, -5, 5, 0, 0, 5, -5, 0], GOLD).setStrokeStyle(0.5, GOLD_DK, 1);
    const insetRuby = scene.add.polygon(0, -3, [0, -3, 3, 0, 0, 3, -3, 0], RUBY).setStrokeStyle(0.4, 0x6a0808, 1);
    const insetShine = scene.add.circle(-1, -4, 0.7, 0xffe0c0, 0.95);
    scene.tweens.add({ targets: insetRuby, alpha: { from: 0.85, to: 1 }, yoyo: true, repeat: -1, duration: 700, ease: "Sine.easeInOut" });

    // ---- GOLDEN CRENELLATIONS WITH SPIRES ----
    const crenL = scene.add.rectangle(-9, -16, 5, 6, STONE_HI).setStrokeStyle(1, STONE_DK, 1);
    const crenM = scene.add.rectangle(0, -16, 5, 6, STONE_HI).setStrokeStyle(1, STONE_DK, 1);
    const crenR = scene.add.rectangle(9, -16, 5, 6, STONE_HI).setStrokeStyle(1, STONE_DK, 1);
    const crenLcap = scene.add.rectangle(-9, -19, 5, 0.8, GOLD);
    const crenMcap = scene.add.rectangle(0, -19, 5, 0.8, GOLD);
    const crenRcap = scene.add.rectangle(9, -19, 5, 0.8, GOLD);

    // Mini gold spires on the corner crenellations
    const spireL = scene.add.triangle(-9, -22, 0, 0, -1.5, 3, 1.5, 3, GOLD).setStrokeStyle(0.4, GOLD_DK, 1);
    const spireR = scene.add.triangle(9, -22, 0, 0, -1.5, 3, 1.5, 3, GOLD).setStrokeStyle(0.4, GOLD_DK, 1);

    // ---- GRAND DOOR ARCH WITH GOLD FRAME ----
    const doorFrame = scene.add.rectangle(0, 11, 11, 10, GOLD).setStrokeStyle(0.5, GOLD_DK, 1);
    const door = scene.add.rectangle(0, 11, 8, 8, 0x2a1a08).setStrokeStyle(0.7, 0x000, 1);
    const doorTopArc = scene.add.arc(0, 7, 4, 180, 360, false, GOLD, 1).setStrokeStyle(0.6, GOLD_DK, 1);
    const doorKnob = scene.add.circle(2, 11, 0.7, GOLD_HI);

    // ---- WINDOW WITH GLOWING SAPPHIRE ----
    const window = scene.add.rectangle(0, -3, 3, 6, 0x000000);
    const windowGlow = scene.add.rectangle(0, -3, 2.4, 5.4, SAPPHIRE_HI, 0.8);
    scene.tweens.add({ targets: windowGlow, alpha: { from: 0.55, to: 1 }, yoyo: true, repeat: -1, duration: 800, ease: "Sine.easeInOut" });

    // ---- CROWN ON TOP (premium signature) ----
    // Central spire (tall)
    const crownSpire = scene.add.triangle(0, -28, 0, 0, -2.5, 6, 2.5, 6, GOLD).setStrokeStyle(0.6, GOLD_DK, 1);
    const crownBall = scene.add.circle(0, -28, 1.4, GOLD_HI).setStrokeStyle(0.4, GOLD_DK, 1);
    // Crown band base
    const crownBand = scene.add.rectangle(0, -22, 18, 2.2, GOLD).setStrokeStyle(0.5, GOLD_DK, 1);
    // 3 crown points
    const crownPL = scene.add.triangle(-7, -25, 0, 0, -1.2, 3, 1.2, 3, GOLD);
    const crownPR = scene.add.triangle(7, -25, 0, 0, -1.2, 3, 1.2, 3, GOLD);
    // Crown gems
    const crownGemL = scene.add.circle(-7, -22, 0.9, SAPPHIRE_HI);
    const crownGemR = scene.add.circle(7, -22, 0.9, RUBY);
    const crownGemC = scene.add.circle(0, -22, 1.0, GOLD_HI);
    scene.tweens.add({ targets: [crownGemL, crownGemR, crownGemC], alpha: { from: 0.7, to: 1 }, yoyo: true, repeat: -1, duration: 950, ease: "Sine.easeInOut" });

    // Banner pole (kept, lifted to attach to crown spire)
    const pole = scene.add.rectangle(0, -22, 1.5, 12, 0x4a3018);

    // Banner (triangular flag — now richer red with gold border)
    this.banner = scene.add.triangle(6, -25, 0, 0, 0, 8, 10, 4, RUBY).setStrokeStyle(1, GOLD, 1);
    this.scene.tweens.add({
      targets: this.banner,
      scaleX: { from: 1, to: 0.92 },
      yoyo: true,
      repeat: -1,
      duration: 600,
      ease: "Sine.easeInOut",
    });
    // Side mini banners for grandeur
    const bannerL = scene.add.triangle(-12, -10, 0, 0, 0, 5, 4, 2.5, GOLD, 0.95).setStrokeStyle(0.5, GOLD_DK, 1);
    const bannerR = scene.add.triangle(12, -10, 0, 0, 0, 5, -4, 2.5, GOLD, 0.95).setStrokeStyle(0.5, GOLD_DK, 1);
    scene.tweens.add({ targets: [bannerL, bannerR], scaleY: { from: 1, to: 0.88 }, yoyo: true, repeat: -1, duration: 700, ease: "Sine.easeInOut" });

    // ---- SPARKLES (4, drifting around throne) ----
    const spark1 = scene.add.star(-22, -8, 4, 0.7, 1.8, GOLD_HI, 0.95);
    const spark2 = scene.add.star(22, -10, 4, 0.7, 1.8, GOLD_HI, 0.95);
    const spark3 = scene.add.star(-18, 12, 4, 0.6, 1.5, GOLD_HI, 0.95);
    const spark4 = scene.add.star(18, 12, 4, 0.6, 1.5, GOLD_HI, 0.95);
    scene.tweens.add({ targets: spark1, scale: { from: 0.5, to: 1.2 }, alpha: { from: 0.3, to: 1 }, yoyo: true, repeat: -1, duration: 880, ease: "Sine.easeInOut" });
    scene.tweens.add({ targets: spark2, scale: { from: 0.6, to: 1.3 }, alpha: { from: 0.3, to: 1 }, yoyo: true, repeat: -1, duration: 1020, ease: "Sine.easeInOut", delay: 220 });
    scene.tweens.add({ targets: spark3, scale: { from: 0.5, to: 1.1 }, alpha: { from: 0.25, to: 0.95 }, yoyo: true, repeat: -1, duration: 1140, ease: "Sine.easeInOut", delay: 440 });
    scene.tweens.add({ targets: spark4, scale: { from: 0.6, to: 1.2 }, alpha: { from: 0.25, to: 0.95 }, yoyo: true, repeat: -1, duration: 960, ease: "Sine.easeInOut", delay: 660 });

    // Crack overlay (drawn on top later when damaged)
    this.cracks = scene.add.graphics();

    this.container.add([
      this.aura,
      this.hpRing,
      shadow,
      shadowInner,
      step1, step1Hi,
      step2, step2Hi,
      base, baseVein1, baseVein2, baseTrim,
      pillarL, pillarR,
      pillarLbase, pillarRbase,
      body, bodyHi,
      trim1, trim2,
      insetGold, insetRuby, insetShine,
      bannerL, bannerR,
      pillarLcap, pillarRcap,
      crenL, crenM, crenR,
      crenLcap, crenMcap, crenRcap,
      spireL, spireR,
      doorFrame, doorTopArc, door, doorKnob,
      window, windowGlow,
      crownBand, crownPL, crownPR,
      crownGemL, crownGemR, crownGemC,
      crownSpire, crownBall,
      pole,
      this.banner,
      spark1, spark2, spark3, spark4,
      this.cracks,
    ]);

    // ---- LUXURY SCALE: bigger, premium throne ----
    this.container.setScale(1.55);
  }

  /** Visual reaction when an enemy leaks and damages the throne. */
  onLeakHit(damage: number, livesAfter: number): void {
    // Shake the throne
    this.scene.tweens.add({
      targets: this.container,
      x: { from: this.container.x - 4, to: this.container.x },
      duration: 200,
      ease: "Quad.easeOut",
    });
    // Red flash overlay
    const flash = this.scene.add.circle(this.container.x, this.container.y, 32, 0xff3030, 0.6);
    this.scene.tweens.add({
      targets: flash,
      scale: 2,
      alpha: 0,
      duration: 280,
      onComplete: () => flash.destroy(),
    });
    // Floating "-N" damage text
    const dmgText = this.scene.add
      .text(this.container.x, this.container.y - 26, `-${damage}`, {
        fontFamily: "system-ui, sans-serif",
        fontSize: "14px",
        fontStyle: "bold",
        color: "#ff3a3a",
        stroke: "#000",
        strokeThickness: 3,
      })
      .setOrigin(0.5)
      .setDepth(200);
    this.scene.tweens.add({
      targets: dmgText,
      y: dmgText.y - 24,
      alpha: 0,
      duration: 800,
      ease: "Quad.easeOut",
      onComplete: () => dmgText.destroy(),
    });

    this.updateHpVisuals(livesAfter);
  }

  /** Update aura/banner color + crack graphics based on remaining HP fraction. */
  updateHpVisuals(livesAfter: number): void {
    const frac = Math.max(0, livesAfter / this.maxLives);

    // Color shift: green → yellow → red
    const color = frac > 0.5 ? 0x4ce053 : frac > 0.25 ? 0xffc23a : 0xff3a3a;
    this.hpRing.setStrokeStyle(3, color, 0.85);
    this.hpRing.setVisible(frac < 1);
    this.aura.fillColor = frac > 0.25 ? 0x4cc9f0 : 0xff3a3a;
    this.aura.setVisible(frac < 0.5);

    // HP ring becomes a partial arc as lives drop.
    const span = 360 * frac;
    this.hpRing.setStartAngle(-90);
    this.hpRing.setEndAngle(-90 + span);

    // Banner droops when HP is critical.
    if (frac < 0.3) {
      this.banner.setRotation(0.4);
    } else {
      this.banner.setRotation(0);
    }

    // Add visible cracks once below half HP.
    this.cracks.clear();
    if (frac < 0.5) {
      this.cracks.lineStyle(1.5, 0x000000, 0.7);
      this.cracks.beginPath();
      this.cracks.moveTo(-6, -10);
      this.cracks.lineTo(-2, -2);
      this.cracks.lineTo(-8, 4);
      this.cracks.strokePath();
    }
    if (frac < 0.25) {
      this.cracks.lineStyle(1.5, 0x000000, 0.85);
      this.cracks.beginPath();
      this.cracks.moveTo(8, -8);
      this.cracks.lineTo(4, 0);
      this.cracks.lineTo(10, 6);
      this.cracks.strokePath();
    }
  }
}
